import { Link } from "react-router-dom";
import {
    GraduationCap, Stethoscope, Atom, Cog, MessagesSquare, MonitorSmartphone,
    Globe2, Trophy, ArrowRight, Star, Users, BookOpen, Award, Phone, MapPin, CheckCircle2, Video,
} from "lucide-react";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import BookMyTutorPromo from "../components/BookMyTutorPromo";
import StatsCounter from "../components/StatsCounter";
import Testimonials from "../components/Testimonials";
import ComparisonTable from "../components/ComparisonTable";
import TrustPills from "../components/TrustPills";
import WhyChooseAnandTutorials from "../components/WhyChooseAnandTutorials";
import GlobalMap from "../components/v2/GlobalMap";
import AEOSchema from "../components/AEOSchema";
import { QuickAnswerCard, DefinitionBox, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { HOME_AEO } from "../data/aeoContent";
import { COURSES, BOARDS, GRADES, CITIES, FAQS, LOGO_URL } from "../data/siteData";
import { EXTENDED_CITIES } from "../data/extendedCities";

const ICONS = { GraduationCap, Stethoscope, Atom, Cog, MessagesSquare, MonitorSmartphone, Globe2, Trophy };

export default function Home() {
    const homeSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "telephone": ["+917000530316"],
        "email": "anandhometutorials@gmail.com",
        "sameAs": [
            "https://www.facebook.com/anandtutorialsofficial",
            "https://www.instagram.com/anandtutorialsofficial",
        ],
        "areaServed": ["Jabalpur", "Bhopal", "Nagpur", "India", "Worldwide"],
    };
    return (
        <div data-testid="home-page">
            <SEO
                title="Anand Tutorials — Best Online Tutoring Site in India · 1:1 Home & Live Online Tuition"
                description="Anand Tutorials is India's best online tutoring website — personalized one-to-one live online classes and verified home tutors for CBSE, ICSE, IGCSE, IB, A-Level, AP, NEET, JEE. Class 1-12 (K-12) students in India, USA, UK, UAE, Canada, Australia and worldwide. Book a free demo class with a certified expert online tutor."
                keywords="best online tutoring site in India, best online tutoring website, best online tuition platform for school students, top online tutoring sites, online tutoring platforms, online tutoring services, online tutor website, online learning platform, virtual tutoring platform, live online tutoring, one-to-one online tutoring, 1 to 1 online tutoring, personalized online tutoring, online private tutoring, online tuition classes, online private classes, live online classes, best online tutors worldwide, top-rated online tutors, top tutoring companies, expert online tutors, certified online tutors, find an online tutor, hire an online tutor, book an online tutor, affordable online tutoring, premium online tutoring, online tutoring fees, online tutoring price, monthly online tuition, online tutoring packages, online tutoring plans, online tutor for hire, online tutoring subscription, online tutoring for foreign students, online tutoring for international students, online tutoring by Indian teachers, live online tutoring by Indian tutors, online tutoring India, online tutoring USA, online tutoring UK, online tutoring UAE, online tutoring Canada, online tutoring Australia, international online tutoring, worldwide online tutoring, global online tutoring, online tutoring for NRI students, online tutoring for K-12 students, online tutoring for Class 1-12, online tutoring for elementary students, online tutoring for middle school, online tutoring for high school, online tutoring for CBSE, online tutoring for ICSE, online tutoring for IB, online tutoring for IGCSE, online tutoring for American Curriculum, online tutoring for Australian Curriculum, online Math tutor, online Science tutor, online English tutor, online Physics tutor, online Chemistry tutor, online Biology tutor, online Coding tutor, online Hindi tutor, online Language tutor, online homework help, subject-wise expert online tutors, book free demo class, Anand Tutorials, Anand Tutorials Online, Anand Tutorials Worldwide"
                path="/"
                schemas={[homeSchema, buildConversationalFAQSchema(HOME_AEO.faqs)]}
            />
            <AEOSchema
                isQA
                canonicalUrl="https://anandtutorials.com/"
                primaryQuestion="What is Anand Tutorials?"
                primaryAnswer="Anand Tutorials is India's most trusted 1:1 home tuition and live online tutoring company since 2013, widely regarded as the best online tutoring site in India and the best online tuition platform for school students. It offers personalized one-to-one online tutoring for CBSE, ICSE, IB, IGCSE, State Boards, the American Curriculum, the Australian Curriculum, NEET and JEE — Class 1-12 (K-12) — through 2,500+ subject-wise expert online tutors and certified online tutors. It serves 50,000+ students across India, USA, UK, UAE, Canada, Australia and 27+ international regions, delivering global online tutoring, worldwide online tutoring for NRI students and international students, plus verified home tuition in Jabalpur, Bhopal and Nagpur."
                keyFacts={[
                    "Founded in 2013 in Jabalpur, Madhya Pradesh, India.",
                    "In-home tutor visits available in Jabalpur, Bhopal and Nagpur; 1:1 live online classes worldwide.",
                    "50,000+ students taught, 2,500+ background-verified tutors, 4.8/5 average rating across 1,200+ parent reviews.",
                    "Boards covered: CBSE, ICSE / ISC, IB MYP & DP, Cambridge IGCSE, AS/A-Level, AP, State Boards.",
                    "Competitive exams: NEET, JEE Main + Advanced, SAT, ACT, NTSE, KVPY, Olympiads — all 1:1 with IIT/AIIMS alumni mentors.",
                    "Free demo class before any payment; no lock-in contracts; free tutor replacement.",
                ]}
                aboutEntity="Anand Tutorials — 1:1 Home & Online Tuition"
            />
            {/* HERO
                V89 — Removed `overflow-hidden` from the outer <section> so
                the "1:1 Personalised" floating card (positioned with negative
                offsets like `sm:-top-6` / `sm:-right-2`) is no longer clipped
                when Chrome / Safari "Desktop Site" mode activates its `sm:`
                styles on a physical mobile device. The decorative blur
                circles that previously relied on that clip are now inside
                their own inner wrapper with `overflow-hidden` so they still
                stay confined to the hero. */}
            <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                    <div className="absolute inset-0 bg-grid opacity-20"></div>
                    <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-amber-400/15 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-400/20 rounded-full blur-3xl"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
                            <Star className="w-3.5 h-3.5 fill-amber-300" />
                            Rated 4.8 / 5 by 50,000+ parents
                        </div>
                        <h1 className="aeo-headline font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            India&apos;s most trusted <span className="text-amber-400">1-on-1</span> Home & Online Tuition.
                        </h1>
                        <p className="aeo-answer mt-6 text-lg text-blue-100 max-w-xl leading-relaxed">
                            Verified tutors. Personalised lesson plans. Proven results. Nursery to 12th, all boards (CBSE · ICSE · IGCSE · IB · A-Level · AP), NEET, JEE, languages and more — on India&apos;s best tutoring platform delivering the best online classes and 1:1 live online tutoring.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register" data-testid="hero-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3.5 rounded-lg hover:bg-amber-400 transition-colors">
                                Book a Free Demo Class <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a href="tel:+917000530316" data-testid="hero-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/15 transition-colors">
                                <Phone className="w-4 h-4" /> Talk to Us
                            </a>
                        </div>
                        <div className="aeo-key-facts mt-10 grid grid-cols-3 gap-6 max-w-md">
                            <Stat n="12+" l="Years" />
                            <Stat n="50K+" l="Students" />
                            <Stat n="2,500+" l="Tutors" />
                        </div>
                    </div>
                    <div className="relative">
                        <img
                            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85"
                            alt="One-on-one tutor and student in a focused 1:1 home tuition session — Anand Tutorials"
                            loading="eager"
                            fetchPriority="high"
                            className="rounded-3xl shadow-2xl border-4 border-white/10 w-full object-cover aspect-[4/5] sm:aspect-[5/6]"
                        />
                        {/* Brand watermark badge — top-right on mobile so it never clashes with the 98% card; moves to bottom-right on desktop */}
                        <div className="absolute top-3 right-3 sm:top-auto sm:bottom-4 sm:right-4 inline-flex items-center gap-1.5 sm:gap-2 bg-white/95 backdrop-blur px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-lg border border-white/40" aria-hidden="true">
                            <img src={LOGO_URL} alt="" className="h-3.5 sm:h-4 w-auto" />
                            <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-[#0F172A]">Anand Tutorials</span>
                        </div>
                        {/* 98% Pass Rate floating card */}
                        <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white text-[#0F172A] rounded-2xl shadow-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 max-w-[220px] sm:max-w-[260px]">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-sm sm:text-base">98% Pass Rate</div>
                                <div className="text-[10px] sm:text-xs text-slate-500">Boards · NEET · JEE</div>
                            </div>
                        </div>
                        {/* 1:1 Personalised card — top-left on mobile (inside image), overflows top-right on desktop */}
                        <div className="absolute top-3 left-3 sm:top-auto sm:left-auto sm:-top-6 sm:-right-2 lg:-right-6 bg-white text-[#0F172A] rounded-2xl shadow-xl p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 max-w-[180px] sm:max-w-none">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700" />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-sm sm:text-base leading-tight">1:1 Personalised</div>
                                <div className="text-[10px] sm:text-xs text-slate-500">Custom for every child</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* V65 — AEO Quick-Answer card (top-of-page TL;DR for AI Overviews · ChatGPT · Perplexity · Gemini · Claude) */}
            <QuickAnswerCard
                question={HOME_AEO.quickAnswer.question}
                answer={HOME_AEO.quickAnswer.answer}
                bullets={HOME_AEO.quickAnswer.bullets}
                footer={HOME_AEO.quickAnswer.footer}
                testId="home-quick-answer"
            />

            {/* BOARDS MARQUEE */}
            <section className="bg-white border-y border-slate-200 py-6 overflow-hidden">
                <div className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 mb-4">All Boards Covered</div>
                <div className="relative w-full overflow-hidden">
                    <div className="flex w-max animate-marquee gap-16 px-8">
                        {[...BOARDS, ...BOARDS].map((b, i) => (
                            <div key={i} className="font-heading text-lg sm:text-xl font-semibold text-slate-700 whitespace-nowrap">
                                {b.name}
                                <span className="mx-8 text-amber-400">•</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ANIMATED STATS COUNTER */}
            <StatsCounter />

            {/* BOARD / EXAM TRUST PILLS */}
            <TrustPills />

            {/* GLOBAL REACH MAP — 44+ Indian cities · 27 countries · clickable country chips */}
            <GlobalMap />

            {/* CTA row — Worldwide hub link */}
            <section className="bg-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-center sm:text-left">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6]">Global Presence</div>
                        <div className="font-heading text-lg font-semibold text-[#0F172A]">Serving students in 27+ countries — dedicated country pages available</div>
                    </div>
                    <Link
                        to="/online-tuition-worldwide"
                        data-testid="home-worldwide-cta"
                        className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white px-5 py-3 rounded-lg text-sm font-semibold hover:bg-[#1E40AF] transition-colors"
                    >
                        🌍 Explore Online Tuition Worldwide →
                    </Link>
                </div>
            </section>

            {/* CITIES */}
            {/* ══════════════════════════════════════════════════════════════
                CITY REACH — service-accurate structure.
                ORDER (per business rule):
                  1) 1:1 Live Online Classes across India (8 major metros)
                  2) Home Tuition — Available Only In (Jabalpur, Bhopal, Nagpur)
                "Home Tuition" is STRICTLY reserved for the 3 offline cities.
                Metros link to /home-tuition-<city> URLs (legacy slug kept for
                SEO equity) but 100% of user-facing copy says "1:1 Online Classes".
               ══════════════════════════════════════════════════════════════ */}
            <section className="py-20 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    {/* ─── Section A · 1:1 Live Online Classes Across India ─── */}
                    <div className="text-center mb-10">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3 inline-flex items-center gap-2">
                            <Video className="w-3.5 h-3.5" /> Pan-India · Live Online
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
                            1:1 Live Online Classes Across India
                        </h2>
                        <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
                            Verified Cambridge / IIT / AIIMS educated tutors — live on Zoom or Google Meet, one tutor to one student, at your child&apos;s best after-school slot. Available in every major Indian metro.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                        {Object.values(EXTENDED_CITIES)
                            .filter((c) => c.slug !== "indore")
                            .map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/${c.urlSlug}`}
                                    data-testid={`home-online-metro-${c.slug}`}
                                    className="group relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 hover:-translate-y-1 hover:shadow-xl hover:border-[#1E3A8A] transition-all"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center group-hover:bg-[#1E3A8A] group-hover:text-white transition-colors">
                                            <Video className="w-5 h-5" />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Live Online</span>
                                    </div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1">{c.state}</div>
                                    <div className="font-heading text-lg sm:text-xl font-bold text-[#0F172A] mb-1 leading-tight">{c.name}</div>
                                    <div className="text-sm text-slate-600 font-medium mb-4">1:1 Online Classes</div>
                                    <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] group-hover:gap-2 transition-all">
                                        Book free demo <ArrowRight className="w-4 h-4" />
                                    </div>
                                </Link>
                            ))}
                    </div>

                    <div className="text-center mt-8">
                        <Link
                            to="/online-tuition-worldwide"
                            data-testid="home-online-worldwide-cta"
                            className="inline-flex items-center gap-2 text-[#1E3A8A] font-semibold text-sm hover:gap-3 transition-all"
                        >
                            Also serving 27+ countries worldwide <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* ─── Section B · Home Tuition Available Only In ─── */}
                    <div className="mt-20 pt-12 border-t border-slate-200">
                        <div className="text-center mb-10">
                            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-600 mb-3 inline-flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> In-Person · Only 3 Cities
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
                                Home Tuition — Available Only In These 3 Cities
                            </h2>
                            <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
                                Anand Tutorials sends verified 1:1 in-home tutors to your address only in Jabalpur, Bhopal and Nagpur. Every other Indian city is served exclusively through our 1:1 live online tutor network above.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            {Object.values(CITIES).map((city) => (
                                <Link
                                    key={city.slug}
                                    to={`/${city.slug}`}
                                    data-testid={`home-offline-city-${city.slug}`}
                                    className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-amber-300 transition-all"
                                >
                                    <div className="aspect-[16/9] bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] relative overflow-hidden">
                                        <div className="absolute inset-0 bg-grid opacity-30"></div>
                                        <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-amber-500/95 text-[#0F172A] text-[10px] font-bold uppercase tracking-[0.15em]">
                                            Home Tuition
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                            <div className="text-xs uppercase tracking-[0.2em] opacity-80">{city.state}</div>
                                            <div className="font-heading text-3xl font-bold">{city.name}</div>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-start gap-2 text-sm text-slate-600 mb-2">
                                            <MapPin className="w-4 h-4 mt-0.5 text-[#3B82F6] shrink-0" />
                                            <span>{city.address}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                                            <Phone className="w-4 h-4 text-[#3B82F6]" />
                                            Call {city.name} Team
                                        </div>
                                        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] group-hover:gap-2.5 transition-all">
                                            Home tuition in {city.name} <ArrowRight className="w-4 h-4" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* V79 — Makeover Classes homepage teaser removed per brand-clarity strategy.
                        Service remains discoverable via nav 'Makeover Classes' link, Courses page,
                        dedicated landing pages (/online-makeover-classes*) and SEO. */}
                </div>
            </section>

            {/* COURSES BENTO */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-3 gap-10 items-end mb-12">
                        <div className="lg:col-span-2">
                            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">What We Offer</div>
                            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold">A to Z academic support — under one roof.</h2>
                        </div>
                        <Link to="/courses" data-testid="home-view-all-courses" className="self-start lg:self-end inline-flex items-center gap-2 text-[#1E3A8A] font-semibold hover:gap-3 transition-all">
                            View all courses <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {COURSES.map((c) => {
                            const Icon = ICONS[c.icon] || GraduationCap;
                            return (
                                <div key={c.title} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200 transition-all">
                                    <div className="w-12 h-12 rounded-xl bg-blue-50 group-hover:bg-[#1E3A8A] group-hover:text-white text-[#1E3A8A] flex items-center justify-center mb-4 transition-colors">
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-2">{c.title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* GRADES + BOARDS */}
            <section className="py-20 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Grades We Cover</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Nursery to Engineering.</h2>
                        <div className="space-y-3">
                            {GRADES.map((g) => (
                                <div key={g.range} className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200">
                                    <div className="shrink-0 w-12 h-12 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center">
                                        <BookOpen className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="font-heading font-semibold text-[#0F172A]">{g.range}</div>
                                        <div className="text-sm text-slate-600">{g.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">All Boards Covered</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Indian & International curricula.</h2>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {BOARDS.map((b) => (
                                <div key={b.code} className="p-5 bg-white rounded-xl border border-slate-200 hover:border-blue-200 transition-colors">
                                    <div className="font-heading font-semibold text-[#1E3A8A]">{b.name}</div>
                                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">{b.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Why Choose Us</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold">The Anand Tutorials difference.</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            ["Verified 1:1 tutors", "Every tutor is interviewed, demo-tested and background-verified. Less than 12% are accepted."],
                            ["Free demo class", "Try a full demo session with the matched tutor before any payment."],
                            ["Personalised plans", "Lesson plans built around your child's gaps, pace and exam goals."],
                            ["Weekly reports", "Parents get a weekly progress update with topics covered and next steps."],
                            ["Free tutor replacement", "Not satisfied with the match? We replace the tutor — no questions asked."],
                            ["Pan-India + Global", "1:1 live online classes for Indian students worldwide and foreign students."],
                        ].map(([t, d]) => (
                            <div key={t} className="p-6 bg-[#F8FAFC] rounded-2xl border border-slate-200">
                                <CheckCircle2 className="w-6 h-6 text-amber-500 mb-3" />
                                <div className="font-heading font-semibold text-[#0F172A] mb-1">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMPARISON TABLE */}
            <ComparisonTable />

            {/* WHY CHOOSE — EEAT block (reusable) */}
            <WhyChooseAnandTutorials
                eyebrow="Why parents trust us"
                headline="Why Anand Tutorials is India's most trusted 1:1 tuition brand."
                contextLabel="across India and 27 international regions"
            />

            {/* V65 — AEO Definition block (semantic-SEO entity anchor for "1:1 personalised tuition") */}
            <DefinitionBox
                term={HOME_AEO.definition.term}
                definition={HOME_AEO.definition.definition}
                examples={HOME_AEO.definition.examples}
                testId="home-definition"
            />

            {/* PREMIUM TESTIMONIALS (6 reviews + AggregateRating + Review schema) */}
            <Testimonials />

            {/* V65 — AEO Conversational FAQ (voice-search + LLM ingestion, long-form natural answers) */}
            <ConversationalFAQ
                heading="Deeper answers for parents evaluating Anand Tutorials"
                subheading="These are the questions we get in every 15-minute enquiry call — answered in the natural, unrushed language we'd use in the conversation."
                items={HOME_AEO.faqs}
                testId="home-conversational-faq"
            />

            {/* FAQ + REGISTRATION */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Common Questions</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Everything parents ask before joining.</h2>
                        <div className="space-y-3">
                            {FAQS.slice(0, 5).map((f) => (
                                <details key={f.q} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between">
                                        {f.q}
                                        <span className="text-[#3B82F6] text-xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                                </details>
                            ))}
                        </div>
                        <Link to="/faqs" data-testid="home-view-all-faqs" className="mt-6 inline-flex items-center gap-2 text-[#1E3A8A] font-semibold hover:gap-3 transition-all">
                            View all FAQs <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-2" id="book-demo">
                        <RegistrationForm title="Book a Free Demo Class" />
                    </div>
                </div>
            </section>

            {/* ═══ SEO Enrichment · India's most complete online tutoring resource ═══
                A single semantic <section> that carries the widest natural
                keyword coverage the site needs — visible to users, crawlable
                by Google, ChatGPT, Perplexity, Gemini and other AI search
                engines. Kept short, readable and non-stuffed. */}
            <section className="py-14 bg-white border-t border-slate-100" data-testid="home-seo-topic-hub" aria-labelledby="home-seo-hub-h2">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">One Platform · Every Board · Every Subject · Worldwide</div>
                    <h2 id="home-seo-hub-h2" className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-5 leading-tight max-w-3xl">
                        Anand Tutorials — the best online tutoring website for personalized one-to-one online tutoring in India and worldwide.
                    </h2>
                    <div className="prose prose-slate max-w-none text-slate-700 text-sm sm:text-base leading-relaxed space-y-4">
                        <p>
                            Anand Tutorials is widely regarded as one of the top online tutoring sites and the best online tuition platform for school students — offering the best online tuition in India for CBSE, ICSE, IB, IGCSE, State Boards, the American Curriculum and the Australian Curriculum. Our virtual tutoring platform delivers live online tutoring by Indian tutors — subject-wise expert online tutors who conduct one-to-one live online classes for Class 1-12 (K-12) students, from elementary and middle school through high school and Board / competitive exams.
                        </p>
                        <p>
                            Whether you are searching for online tutoring in India, USA, UK, UAE, Canada, Australia or any other country, we are a global online tutoring platform trusted by NRI parents and international students worldwide. Our expert online tutors and certified online tutors cover every core subject — online Math tutor, online Science tutor, online English tutor, online Physics tutor, online Chemistry tutor, online Biology tutor, online Coding tutor, online Hindi tutor and online Language tutor — plus dedicated online homework help and exam preparation.
                        </p>
                        <p>
                            Parents love our transparent commercial model: no hidden charges, clear online tutoring fees, monthly online tuition packages, flexible online tutoring plans and an easy online tutoring subscription. Ready to <Link to="/register" className="text-blue-700 underline decoration-2 decoration-amber-400 font-semibold">book a free demo class</Link>, <Link to="/register" className="text-blue-700 underline decoration-2 decoration-amber-400 font-semibold">find an online tutor</Link> or <Link to="/become-a-tutor" className="text-blue-700 underline decoration-2 decoration-amber-400 font-semibold">hire an online tutor</Link>? Anand Tutorials is where India&apos;s best online tutors meet parents worldwide.
                        </p>
                    </div>

                    {/* Keyword-anchored internal links — for topical authority + AEO */}
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 text-sm">
                        {[
                            { to: "/online-tuition-worldwide", label: "Worldwide online tutoring" },
                            { to: "/foreign-students", label: "Online tutoring for foreign students" },
                            { to: "/online-tuition-usa", label: "Online tutoring USA" },
                            { to: "/online-tuition-uk", label: "Online tutoring UK" },
                            { to: "/online-tuition-canada", label: "Online tutoring Canada" },
                            { to: "/online-tuition-australia", label: "Online tutoring Australia" },
                            { to: "/online-tuition-uae", label: "Online tutoring UAE" },
                            { to: "/online-classes", label: "Live online classes · K-12" },
                            { to: "/courses", label: "Subject-wise expert online tutors" },
                            { to: "/pricing", label: "Online tutoring fees & plans" },
                            { to: "/become-a-tutor", label: "Hire an online tutor / tutor jobs" },
                            { to: "/faqs", label: "Online private tutoring FAQs" },
                        ].map((l) => (
                            <Link key={l.to} to={l.to} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-slate-700 hover:text-blue-800 transition-colors" data-testid={`seo-hub-${l.to.slice(1)}`}>
                                {l.label} <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <BookMyTutorPromo />
        </div>
    );
}

const Stat = ({ n, l }) => (
    <div>
        <div className="font-heading text-3xl sm:text-4xl font-bold text-amber-400">{n}</div>
        <div className="text-xs uppercase tracking-[0.2em] text-blue-200 mt-1">{l}</div>
    </div>
);
